import React, { useState, useEffect , useRef} from "react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { FiX, FiCamera, FiChevronRight } from "react-icons/fi";
import PetDetailsHeader from "./petDetailsHaeder";
import SelectAddOnsDrawer from "../Orders/SelectAddOnsDrawer";
import ProductCatalog from "../../components/common/ProductCatalog";
import OtherChargesDrawer from "../../components/common/OtherChargesDrawer";
import ServiceDetailsCard from "./ServiceDetailsCard";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { uploadFileToS3 } from "../../components/common/uploadService";
import Swal from "sweetalert2";
import config from "../../config";

// ─── Small presentational helpers ──────────────────────────────────────────
const SectionCard = ({ title, action, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-2 border-b border-slate-100 bg-slate-50">
      <h3 className="text-sm font-bold text-slate-700">{title}</h3>
      {action}
    </div>
    <div className="p-3">{children}</div>
  </div>
);

// ─── Caption block below the photo — clamps to 2 lines, expands downward ──
const ReadMoreCaption = ({ text, lines = 2 }) => {
  const [expanded, setExpanded] = useState(false);
  const [needsClamp, setNeedsClamp] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setNeedsClamp(textRef.current.scrollHeight > textRef.current.clientHeight + 1);
    }
  }, [text]);

  if (!text) return null;

  return (
    <div className="px-3 py-2.5">
      <p
        ref={textRef}
        className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap"
        style={
          expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: lines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
        }
      >
        {text}
      </p>
      {(needsClamp || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 mt-1"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

const EntryCard = ({ photos = [], note, onPreviewPhoto }) => {
  if (!photos.length && !note) return null;

  return (
    <div
      className="rounded-xl overflow-hidden border border-slate-200 bg-white"
      style={{ width: 220 }}
    >
      {photos.length === 1 && (
        <button
          type="button"
          onClick={() => onPreviewPhoto({ url: toDisplayUrl(photos[0]), note })}
          className="block w-full"
          style={{ height: 160 }}
        >
          <img
            src={toDisplayUrl(photos[0])}
            alt=""
            className="w-full h-full object-cover"
          />
        </button>
      )}

      {photos.length > 1 && (
        <div className="grid grid-cols-2 gap-px bg-slate-200" style={{ height: 160 }}>
          {photos.slice(0, 4).map((photo, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onPreviewPhoto({ url: toDisplayUrl(photo), note })}
              className="relative block overflow-hidden"
            >
              <img
                src={toDisplayUrl(photo)}
                alt=""
                className="w-full h-full object-cover"
              />
              {idx === 3 && photos.length > 4 && (
                <span className="absolute inset-0 bg-black/55 text-white text-[11px] font-semibold flex items-center justify-center">
                  +{photos.length - 4}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {note && <ReadMoreCaption text={note} />}
    </div>
  );
};


const s3Base = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL || "";
const toDisplayUrl = (key) => {
  if (!key) return "";
  return key.startsWith("http") ? key : `${s3Base}/${key}`;
};

const GroomingStartedDrawer = ({ open, booking, onClose, appointmentRow, onSave, onComplete }) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const customer = booking?.customer || {};
  const service = booking?.service || {};
  const pet = booking?.pet || {};
  const raw = appointmentRow || {};
  const refId = String(raw.ref_id || raw._id || booking?.booking_id || "");

  // ── Fixed / view-only header details ────────────────────────────────────
  const clientName =
    customer.name ||
    `${raw.client_first_name || ""} ${raw.client_last_name || ""}`.trim() ||
    "—";
  const clientPhone = customer.phone || raw.phone || "";
  const clientEmail = customer.email || raw.email || "";
  const clientId = booking?.pet?.vendor_client_id || raw.vendor_client_id || "";
  const staffName = service.staffName || raw.employee_name || "";
  const orderId = booking?.order_id || raw.order_id || "";
  const vendorPetId = booking?.pet?.vendor_pet_id || raw.vendor_pet_id || raw.pet_id || "";
  
  const [groomingOrderItem, setGroomingOrderItem] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [headerRefreshKey, setHeaderRefreshKey] = useState(0);
  const [serviceStatus, setServiceStatus] = useState(
    () => booking?.status || raw?.status || "",
  );

  const isServiceCompleted = ["completed", "complete", "closed", "checkout", "checked_out"].includes(
    String(serviceStatus || "").toLowerCase().trim(),
  );

  const petDetails = {
    name: pet.name || raw.pet_name || "",
    breed: pet.breed || raw.pet_breed || "",
    size: pet.size || raw.pet_size || "",
    type: pet.type || raw.pet_type || "",
  };

  // serviceDetails needs to be state, since editing writes back to it
  const [serviceDetails, setServiceDetails] = useState({
    name: service.service_name || raw.service_name || "",
    duration: service.duration || raw.duration || "",
    date: service.start_date || raw.start_date || "",
    time: service.start_time || raw.start_time || "",
    price:
        service.total_price ||
      service.unit_price_with_tax ||
      raw.total_price ||
      raw.unit_price_with_tax ||
      0,
  });

  const serviceCategoryTypeId =
    raw.service_category_type_id ||
    raw.service_type_id ||
    service.service_category_type_id;

  const serviceCategory =
    service.service_category_name ||
    raw.service_category_name ||
    raw.service_type_name ||
    "";

  // ── Add-ons / Products / Other Charges ──────────────────────────────────
  const [addons, setAddons] = useState([]);
  const [products, setProducts] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);

  const [isAddonsDrawerOpen, setIsAddonsDrawerOpen] = useState(false);
  const [isProductsDrawerOpen, setIsProductsDrawerOpen] = useState(false);
  const [isOtherChargesDrawerOpen, setIsOtherChargesDrawerOpen] = useState(false);

  // ── Photos / Notes: existing history (read-only) vs. new entry (editable) ──
  //const [existingPhotos, setExistingPhotos] = useState([]); // display URLs, newest first
  //const [existingNotes, setExistingNotes] = useState([]);   // [{ description, logged_at }], newest first
  const [checkinPhotos, setCheckinPhotos] = useState([]);
  const [checkinNotes, setCheckinNotes] = useState("");

  
  const [activities, setActivities] = useState([]);  
  const [newPhotos, setNewPhotos] = useState([]);           // S3 keys from PhotoUploader — this session's upload
  const [newNoteText, setNewNoteText] = useState("");       // this session's note — this is what gets saved
  
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const markChanged = () => setHasChanges(true);
  const [issingleserviceOrder, setIsSingleServiceOrder] = useState(false);
  const [orderType, setOrderType] = useState(null);


   // 2. Fetch the order and restore addons/products/otherCharges — mirrors Boarding's fetchOrder
  const fetchOrder = async () => {
    if (!orderId) return;
    try {
      setOrderLoading(true);
      const res = await httpService.get(`/vendororder/getvendororder/${orderId}`);
      if (res?.code === 200 && res?.data) {
        setOrderType(res.data.order_type);
        const items = Array.isArray(res.data.order_items) ? res.data.order_items : [];
        setOrderItems(items);

        const matchedItem =
         // items.find((item) => item?.ref_id === raw.ref_id && item?.vendor_pet_id === vendorPetId) ||
          items.find((item) => item?.ref_id === refId && item?.vendor_pet_id === vendorPetId) ||
         items.find((item) => item?.vendor_pet_id === vendorPetId && item?.item_type === "grooming") ||
          items.find((item) => item?.service_id === raw?.service_id && item?.vendor_pet_id === vendorPetId);

        if (matchedItem) {
          setGroomingOrderItem(matchedItem);
          if (matchedItem.status) setServiceStatus(matchedItem.status);
          else if (res.data.status) setServiceStatus(res.data.status);
          if (matchedItem.addons) setAddons(matchedItem.addons);
          if (matchedItem.products) setProducts(matchedItem.products);
          if (matchedItem.otherCharges) setOtherCharges(matchedItem.otherCharges);

          setServiceDetails((prev) => ({
            ...prev,
            name: matchedItem.service_name || prev.name,
            duration: matchedItem.duration ?? prev.duration,
            price: matchedItem.total_price || matchedItem.final_total || matchedItem.price || prev.price,
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching order:", err);
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !orderId) return;
    fetchOrder();
  //}, [open, orderId, raw.ref_id, vendorPetId]);
   }, [open, orderId, refId, vendorPetId]);

  useEffect(() => {
    if (!open) return;
    // Seed from props on open; fetchOrder overwrites with live order status.
    setServiceStatus(booking?.status || raw?.status || "");
  }, [open]);

  // 3. Persist addon/product/charge edits back to the order — mirrors Boarding's saveAdditionalChargesToOrder
  const saveAdditionalChargesToOrder = async (updatedAddons, updatedProducts, updatedCharges) => {
    if (!orderId || !vendorPetId) return false;
    try {
      const currentOrderItem =
        groomingOrderItem ||
        orderItems.find((item) => item?.vendor_pet_id === vendorPetId && item?.item_type === "grooming");
      if (!currentOrderItem) return false;

      const payload = {
        order_id: orderId,
        vendor_client_id: clientId,
        order_type: orderType,
        order_items: [
          {
            ...currentOrderItem,
            addons: updatedAddons !== undefined ? updatedAddons : addons,
            products: updatedProducts !== undefined ? updatedProducts : products,
            otherCharges: updatedCharges !== undefined ? updatedCharges : otherCharges,
          },
        ],
      };

      const response = await httpService.post("/vendororder/addVendorOrderOrUpdate", payload);
      if (response?.code === 200 || response?.status === 200) {
        await fetchOrder();
        setHeaderRefreshKey((prev) => prev + 1);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error saving additional charges to order:", error);
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to save additional charges",
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }
  };
    
  // ── Fetch existing check-in + activity history on open ──────────────────
  useEffect(() => {

    if (!open) return;
    //const refId = raw.ref_id;
    if (!refId) return;

    const fetchHistory = async () => {
      console.log("fetchHistory called");
      setIsLoadingHistory(true);
      setNewPhotos([]);
      setNewNoteText("");
      setCheckinPhotos([]);
      setActivities([]);

      try {
        const [checkinRes, activitiesRes, isSingleServiceOrderRes] = await Promise.allSettled([
          httpService.get(`/checkin/grooming/${refId}`),
          httpService.get(`/serviceactivity/grooming/${refId}/activities`),
          httpService.post(`/vendororder/isSingleServiceOrder`,{orderId}),
        ]);
        if(isSingleServiceOrderRes?.value?.status)
          setIsSingleServiceOrder(isSingleServiceOrderRes?.value?.data?.isSingleServiceOrder);
        else
          setIsSingleServiceOrder(false);
        const checkinData =
          checkinRes.status === "fulfilled" ? checkinRes.value?.data?.checkin_data : null;
        const activitiesRaw =
          activitiesRes.status === "fulfilled"
            ? activitiesRes.value?.data?.logs ?? []
            : [];
        
        const sortedActivities = Array.isArray(activitiesRaw)
          ? [...activitiesRaw].sort(
              (a, b) => new Date(b.logged_at) - new Date(a.logged_at)
            )
          : [];
        setCheckinPhotos(checkinData?.photos || []);
        setCheckinNotes(checkinData?.notes || "");
        setActivities(sortedActivities);
      } catch (err) {
        console.error("Failed to fetch grooming history:", err);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchHistory();
 // }, [open, raw.ref_id]);
  }, [open, refId]);

  // ── Add-ons ──────────────────────────────────────────────────────────
  const openAddonsDrawer = () => {
    if (isServiceCompleted) return;
    setIsAddonsDrawerOpen(true);
  };
  const closeAddonsDrawer = () => setIsAddonsDrawerOpen(false);
  
  const handleSaveAddons = async (updatedAddons) => {
    setIsAddonsDrawerOpen(false);
    if (!updatedAddons) return;
    setAddons(updatedAddons);
    markChanged();
    await saveAdditionalChargesToOrder(updatedAddons, undefined, undefined);
  };

  const handleDeleteAddon = async (addonIndex) => {
    const updated = addons.filter((_, i) => i !== addonIndex);
    setAddons(updated);
    markChanged();
    await saveAdditionalChargesToOrder(updated, undefined, undefined);
  };
  
   // ── Products ─────────────────────────────────────────────────────────
  const openProductsDrawer = () => {
    if (isServiceCompleted) return;
    setIsProductsDrawerOpen(true);
  };
  const closeProductsDrawer = () => setIsProductsDrawerOpen(false);
  const handleSaveProducts = async (updatedProducts) => {
    setIsProductsDrawerOpen(false);
    if (!updatedProducts) return;
    
    const roundToTwo = (num) => {
      if (num === null || num === undefined || isNaN(num)) return 0;
      return Math.round((parseFloat(num) + Number.EPSILON) * 100) / 100;
    };

    const formattedProducts = updatedProducts.map((product) => {
      // Get values from product (using the correct field names from ProductCatalog)
      const effectivePrice = Number(product.sale_price_after_discount || product.effective_sale_price || product.price || 0);
      const mrpPrice = Number(product.mrp || product.mrp_price || product.sale_price || 0);
      const gstSlab = Number(product.gst_slab || product.tax_percentage || 0);
      const quantity = Number(product.quantity || 1);
      
      // ✅ CRITICAL: Calculate price without tax
      let priceWithoutTax = effectivePrice;
      let gstAmount = 0;
      
      if (gstSlab > 0 && effectivePrice > 0) {
        // priceWithoutTax = effectivePrice / (1 + gstSlab/100)
        priceWithoutTax = effectivePrice / (1 + gstSlab / 100);
        gstAmount = effectivePrice - priceWithoutTax;
      }
      
      return {
        // Preserve all original product data first
        ...product,

        // Core identifiers — these must come AFTER the spread to win
        product_id: product.id || product._id,
        product_name: product.name,

        // Quantity
        quantity: quantity,

        // ✅ CRITICAL: These fields MUST be correct
        unit_price: roundToTwo(effectivePrice),
        price: roundToTwo(priceWithoutTax),
        mrp_price: roundToTwo(mrpPrice),
        sale_price: roundToTwo(mrpPrice),
        total_price: roundToTwo(effectivePrice * quantity),
        
        // GST fields
        gst: roundToTwo(gstAmount),
        gstAmount: roundToTwo(gstAmount),
        gstAmountAfterDiscount: roundToTwo(gstAmount),
        tax_percentage: gstSlab,
        tax_amount: roundToTwo(gstAmount),
        
        // Amount breakdowns
        amount_without_tax: roundToTwo(priceWithoutTax),
        amount_with_tax: roundToTwo(effectivePrice),
        amount_before_discount: roundToTwo(mrpPrice),
        amount_after_discount: roundToTwo(effectivePrice),
        
        // Additional fields
        description: product.description || "",
        sku_number: product.sku_number || "",
        gst_slab: gstSlab,
      };
    });

    const formatted = updatedProducts.map((p) => ({
      ...p,
      item_type: "product",
      product_id: p.id || p.product_id,
      product_name: p.name || p.product_name,
      quantity: p.quantity || 1,
      unit_price: p.unit_price ?? p.price ?? 0,
      total_price: (p.unit_price ?? p.price ?? 0) * (p.quantity || 1),
    }));

    const merged = [...products];
    formattedProducts.forEach((item) => {
      const existingIndex = merged.findIndex((e) => e.product_id === item.product_id);
      if (existingIndex !== -1) {
        const existing = merged[existingIndex];
        const newQty = (existing.quantity || 0) + (item.quantity || 1);
        merged[existingIndex] = {
          ...existing,
          quantity: newQty,
          total_price: (existing.unit_price || 0) * newQty,
          amount_without_tax: item.amount_without_tax * newQty,
        };
      } else {
        merged.push(item);
      }
    });

    setProducts(merged);
    markChanged();
    await saveAdditionalChargesToOrder(undefined, merged, undefined);
  };

  const handleDeleteProduct = async (productIndex) => {
    const updated = products.filter((_, i) => i !== productIndex);
    setProducts(updated);
    markChanged();
    await saveAdditionalChargesToOrder(undefined, updated, undefined);
  };

  const handleIncrementProduct = async (productIndex) => {
    const updated = [...products];
    const p = updated[productIndex];
    updated[productIndex] = { ...p, quantity: (p.quantity || 1) + 1 };
    setProducts(updated);
    markChanged();
    await saveAdditionalChargesToOrder(undefined, updated, undefined);
  };

  const handleDecrementProduct = async (productIndex) => {
    const updated = [...products];
    const p = updated[productIndex];
    if ((p.quantity || 1) <= 1) return;
    updated[productIndex] = { ...p, quantity: p.quantity - 1 };
    setProducts(updated);
    markChanged();
    await saveAdditionalChargesToOrder(undefined, updated, undefined);
  };

  // ── Other Charges ────────────────────────────────────────────────────
  const openOtherChargesDrawer = () => {
    if (isServiceCompleted) return;
    setIsOtherChargesDrawerOpen(true);
  };
  const closeOtherChargesDrawer = () => setIsOtherChargesDrawerOpen(false);
  const handleSaveOtherCharges = async (updatedCharges) => {
    setIsOtherChargesDrawerOpen(false);
    if (!updatedCharges) return;
    setOtherCharges(updatedCharges);
    markChanged();
    await saveAdditionalChargesToOrder(undefined, undefined, updatedCharges);
  };

  const handleDeleteOtherCharge = async (chargeIndex) => {
    const updated = otherCharges.filter((_, i) => i !== chargeIndex);
    setOtherCharges(updated);
    markChanged();
    await saveAdditionalChargesToOrder(undefined, undefined, updated);
  };
  // ── Close / Save ─────────────────────────────────────────────────────
   const handleClose = async () => {
    if (!hasChanges) {
      onClose?.();
      return;
    }

    const result = await Swal.fire({
      title: "Unsaved Changes",
      text: "You have unsaved changes. What would you like to do?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Save & Close",
      confirmButtonColor: "#4f46e5",
      cancelButtonText: "Close Anyway",
      cancelButtonColor: "#dc2626",
    });

    if (result.isConfirmed) {
      const saveSuccess = await handleSave();
      if (saveSuccess) {
        onClose?.();
      }
      return;
    }

    if (result.dismiss === Swal.DismissReason.cancel) {
      onClose?.();
    }
  };

   const handleComplete = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "All changes will be saved before completing this appointment.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, complete it!",
    });

    if (!result.isConfirmed) return;

    try {
      setIsSaving(true);

      if (hasChanges) {
        const saveSuccess = await handleSave();
        if (!saveSuccess) {
          throw new Error("Failed to save changes");
        }
      }

      // const completeResponse = await httpService.put(
      //   `/vendorappointment/updateVendorAppointmentStatus/${raw.ref_id}`,
      //   { status: "completed" }
      // );
      const completeResponse = await httpService.put(
        `/vendorappointment/updateVendorAppointmentStatus/${refId}`,
        { status: "completed" }
      );

      if (completeResponse?.code !== 200 && completeResponse?.status !== 200) {
        throw new Error(completeResponse?.message || "Failed to complete appointment");
      }

      setServiceStatus("completed");

      if (issingleserviceOrder) {
        onClose?.();
        onComplete?.(orderId);
      } else {
        onClose?.();
      }
    } catch (error) {
      console.error("Error completing appointment:", error);
      Swal.fire({
        title: "Error",
        text: error?.message || "Failed to save changes or complete appointment.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
   const handleSave = async () => {
    setIsSaving(true);
    try {
      const newPhotoUrls = newPhotos.map(toDisplayUrl);

      const activityPayload = {
        activity_type: "log",
        time_slot: " ",
        description: newNoteText,
        photos: newPhotoUrls,
        logged_at: new Date().toISOString(),
      };

      // await httpService.post(
      //   `/serviceactivity/grooming/${raw.ref_id}/activities`,
      //   activityPayload
      // );
      await httpService.post(
        `/serviceactivity/grooming/${refId}/activities`,
        activityPayload
      );

      // Also sync photos and notes to /notes/updatenote so they appear in Client Pet Photos & Notes section
      const targetClientId = clientId || groomingOrderItem?.vendor_client_id;
      if (targetClientId) {
        if (newPhotoUrls.length > 0) {
          await httpService.post("/notes/updatenote", {
            type: "photo",
            noteText: "",
            vendor_id: groomingOrderItem?.vendor_id || "",
            vendor_client_id: targetClientId,
            vendor_pet_id: vendorPetId,
            photos: newPhotoUrls,
          }).catch((e) => console.error("Error saving activity photo note:", e));
        }

        if (newNoteText && newNoteText.trim().length > 0) {
          await httpService.post("/notes/updatenote", {
            type: "note",
            noteText: newNoteText.trim(),
            vendor_id: groomingOrderItem?.vendor_id || "",
            vendor_client_id: targetClientId,
            vendor_pet_id: vendorPetId,
            photos: [],
          }).catch((e) => console.error("Error saving activity note:", e));
        }
      }

      setActivities((prev) => [
        {
          _id: `local-${Date.now()}`,
          description: newNoteText,
          photos: newPhotoUrls,
          logged_at: activityPayload.logged_at,
        },
        ...prev,
      ]);

      setNewNoteText("");
      setNewPhotos([]);

      const payload = {
       // ref_id: raw.ref_id,
        ref_id: refId,
        order_id: orderId,
        petDetails,
        serviceDetails,
        addons,
        products,
        otherCharges,
        notes: newNoteText,
      };

      setHasChanges(false);
      onSave?.(payload);
      return true;                 // ← was missing
    } catch (err) {
      console.error("Failed to save grooming activity:", err);
      return false;                // ← was missing
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  const addonsTotal = addons.reduce(
    (sum, a) => sum + parseFloat(a.addon_price || a.price || 0),
    0,
  );
  const productsTotal = products.reduce(
    (sum, p) => sum + (parseFloat(p.price ?? p.unit_price ?? 0) * (p.quantity || 1)),
    0,
  );
  const chargesTotal = otherCharges.reduce(
    (sum, c) => sum + parseFloat(c.amount || 0),
    0,
  );
  const totalCharges = addonsTotal + productsTotal + chargesTotal;

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (newPhotos.length + files.length > 10) {
      toast.error("Maximum 10 photos allowed");
      return;
    }

    setIsUploadingPhoto(true);
    try {
      for (const file of files) {
        const s3Key = await uploadFileToS3(file, {
          returnType: "s3Key",
          module: "activity",
          docType: "photo",
        });
        if (s3Key) {
          setNewPhotos((prev) => [...prev, s3Key]);
        } else {
          toast.error(`${file.name} upload failed`);
        }
      }
      markChanged();
    } catch (err) {
      console.error("Photo upload failed:", err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handleRemoveNewPhoto = (idx) => {
    setNewPhotos((prev) => prev.filter((_, i) => i !== idx));
    markChanged();
  };
  
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div
        className="fixed right-0 top-0 h-full z-50 flex flex-col bg-slate-50 shadow-2xl w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <PetDetailsHeader
          key={headerRefreshKey}
          orderId={orderId}
          vendorPetId={vendorPetId}
          activeServiceRefId={refId}
          clientName={clientName}
          clientPhone={clientPhone}
          clientEmail={clientEmail}
          staffName={staffName}
          clientId={clientId}
          showOnlySelectedPet={true}
          onClose={handleClose}
        />

        {/* Body */}
        <div className="flex-1 overflow-auto p-2 sm:p-2 space-y-2">
          <ServiceDetailsCard
            serviceCategory={serviceCategory}
            serviceDetails={serviceDetails}
            staffName={staffName}
            addons={addons}
            products={products}
            otherCharges={otherCharges}
            onOpenProductsDrawer={openProductsDrawer}
            onOpenAddonsDrawer={openAddonsDrawer}
            onOpenOtherChargesDrawer={openOtherChargesDrawer}
            onDeleteAddon={handleDeleteAddon}
            onDeleteProduct={handleDeleteProduct}
            onIncrementProduct={handleIncrementProduct}
            onDecrementProduct={handleDecrementProduct}
            onDeleteOtherCharge={handleDeleteOtherCharge}
            rawData={raw}
            orderId={orderId}
            clientId={clientId}
            clientName={clientName}
            clientPhone={clientPhone}
            vendorPetId={vendorPetId}
            readOnly={isServiceCompleted}
            onServiceUpdated={(updated) => {
              if (isServiceCompleted) return;
              setServiceDetails((prev) => ({
                ...prev,
                name: updated.service_name || prev.name,
                duration: updated.duration || prev.duration,
                price: updated.unit_price_with_tax ?? prev.price,
              }));
              markChanged();
            }}
          />
           
           {/* One entry = photo(s) + note, shown together so the relationship is obvious */}
            <SectionCard title="Add Activity">
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-3 text-sm text-slate-400">
                  Loading…
                </div>
              ) : (
                <div className={`flex -mt-2 ${isServiceCompleted ? "pointer-events-none opacity-60" : ""}`}>
                  {/* Photos half */}
                  <div className="flex-1 pr-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                        Photos
                      </h4>
                      <span className="text-[11px] text-slate-400">
                        {newPhotos.length} / 10
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2.5 items-centre">
                      {newPhotos.map((key, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={toDisplayUrl(key)}
                            alt=""
                            style={{ width: 52, height: 52 }}
                            className="object-cover rounded-lg border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveNewPhoto(idx)}
                            className="absolute -top-1.5 -right-1.5 w-[16px] h-[16px] flex items-center justify-center bg-slate-800 text-white rounded-full"
                          >
                            <FiX className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                      {newPhotos.length < 10 && (
                        <label
                          style={{ width: 72, height: 72 }}
                          className="flex flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-500 cursor-pointer transition-colors"
                        >
                          {isUploadingPhoto ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500" />
                          ) : (
                            <>
                              <FiCamera className="w-10 h-10" />
                              <span className="text-[11px] font-medium leading-none">Add</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handlePhotoSelect}
                            disabled={isUploadingPhoto}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="w-px bg-slate-200 my-1" />

                  {/* Notes half */}
                  <div className="flex-1 pl-4 flex flex-col">
                    <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                      Notes
                    </h4>
                    <textarea
                      value={newNoteText}
                      onChange={(e) => {
                        setNewNoteText(e.target.value);
                        markChanged();
                      }}
                      placeholder="Add grooming notes…"
                      className="w-full flex-1 min-h-[50px] rounded-lg border border-slate-200 p-2.5 text-sm resize-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              )}
            </SectionCard>
            
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center py-6 text-sm text-slate-400">
                    Loading summary...
                  </div>
                ) : checkinPhotos.length === 0 && !checkinNotes && activities.length === 0 ? (
                  <div className="text-sm text-slate-500 py-3">No activity found.</div>
                ) : (
                  <div>
                    {(checkinPhotos.length > 0 || checkinNotes) && (
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={() => setIsCheckinOpen((v) => !v)}
                          className="flex items-center gap-1.5 text-indigo-600 text-[13px] font-semibold hover:text-indigo-700"
                        >
                          <FiChevronRight
                            className={`w-3.5 h-3.5 transition-transform ${isCheckinOpen ? "rotate-90" : ""}`}
                          />
                          Check-in photos and notes
                        </button>
                        {isCheckinOpen && (
                          <div className="mt-2 ml-5 flex flex-wrap items-start gap-3">
                           <EntryCard photos={checkinPhotos} note={checkinNotes} onPreviewPhoto={setPreviewPhoto} />
                          </div>
                        )}
                      </div>
                    )}
                    {activities.length > 0 && (
                    <div className="space-y-4">
                      {activities.some((a) => a.photos?.length > 0) && (
                        <div>
                          <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                            Activity log
                          </h4>
                          <div className="flex flex-wrap items-start gap-3">
                            {activities
                              .filter((a) => a.photos?.length > 0)
                              .map((activity, idx) => (
                                <EntryCard
                                  key={activity._id || idx}
                                  photos={activity.photos}
                                  note={activity.description}
                                  onPreviewPhoto={setPreviewPhoto}
                                />
                              ))}
                          </div>
                        </div>
                      )}

                      {activities.some((a) => !(a.photos?.length > 0) && a.description) && (
                        <div>
                          <h4 className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                            Notes
                          </h4>
                          <div className="space-y-1.5">
                            {activities
                              .filter((a) => !(a.photos?.length > 0) && a.description)
                              .map((activity, idx) => (
                                <p
                                  key={activity._id || idx}
                                  className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2"
                                >
                                  {activity.description}
                                </p>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                )}
              </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {hasChanges ? (
              <span className="flex items-center gap-1.5 text-[11px] text-amber-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Unsaved changes
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-[11px] text-emerald-600 font-semibold">
                <CheckCircleIcon className="w-3.5 h-3.5" />
                All saved
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            {/* <button
              onClick={handleClose}
              className="px-4 py-2 border border-slate-200 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Close
            </button> */}
            
            {!isServiceCompleted ? (
              <button
                onClick={handleComplete}
                className="px-4 py-2 border border-slate-200 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Complete
              </button>
            ) : (
              <button
                disabled
                className="px-4 py-2 bg-slate-200 text-slate-500 rounded-xl text-[12px] font-semibold cursor-not-allowed"
              >
                Completed
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges || isServiceCompleted}
              className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-[12px] font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Real Add-ons drawer */}
      {isAddonsDrawerOpen && (
        <SelectAddOnsDrawer
          isOpen={isAddonsDrawerOpen}
          onClose={closeAddonsDrawer}
          onSave={handleSaveAddons}
          serviceCategoryId={serviceCategoryTypeId}
          petType={petDetails.type || "Both"}
          petTypeId={raw.pet_type_id}
          selectedAddons={addons}
          serviceTypeName={serviceDetails.name}
        />
      )}

      {/* Real Products / Inventory drawer */}
      {isProductsDrawerOpen && (
        <ProductCatalog
          isOpen={isProductsDrawerOpen}
          onClose={closeProductsDrawer}
          onCartUpdate={handleSaveProducts}
          apiEndpoint="/vendorproduct/getAvailableInventoryProducts"
        />
      )}

      {/* Real Other Charges drawer */}
      {isOtherChargesDrawerOpen && (
        <OtherChargesDrawer
          isOpen={isOtherChargesDrawerOpen}
          onClose={closeOtherChargesDrawer}
          onSave={handleSaveOtherCharges}
          initialCharges={otherCharges}
        />
      )}
      
      {previewPhoto && (
      <div
        className="fixed inset-0 z-[70] bg-black/75 flex items-center justify-center p-6"
        onClick={() => setPreviewPhoto(null)}
      >
        <div
          className="bg-white rounded-xl overflow-hidden flex flex-col"
          style={{ maxWidth: "min(90vw, 640px)", maxHeight: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-1 min-h-0 flex items-center justify-center bg-slate-900">
            <img
              src={previewPhoto.url}
              alt=""
              style={{ maxHeight: "72vh", maxWidth: "100%" }}
              className="object-contain"
            />
          </div>
            {previewPhoto.note && (
              <div className="px-5 py-4 overflow-y-auto" style={{ maxHeight: "22vh" }}>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {previewPhoto.note}
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPreviewPhoto(null)}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
};

export default GroomingStartedDrawer;